import { createParams } from 'api/api-utils';
import { Pagination } from 'api/pagination';
import queryApi from 'api/query';
import { ASBV } from 'api/query/asbv/asbv';
import { CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { FiltersState } from 'pages/search/filters';
import { basicDefaultState, BasicFilter, getBreedOrDefault } from 'pages/search/filters/all/basic';
import { locationDefaultState } from 'pages/search/filters/all/location/location-utils';
import { FilterCategory } from 'pages/search/filters/filter-category.enum';
import { individualDefaultState } from 'pages/search/filters/individual/individual-utils';
import { UserType } from 'pages/search/filters/user-type.enum';
import { SortDir } from 'pages/search/results/sort-dir.enum';
import { IndividualFilter } from 'pages/shared/individual-filter.enum';
import { Database } from 'shared/database.enum';
import { SubIndex } from 'shared/sub-index.enum';
import {
  addCustomASBVs, getFilterParams, getParamsByAsbvFilter, getParamsByFilter
} from '../query-utils';
import { SearchParams } from './search-params';
import { SearchParamsIds } from './search-params-ids';
import { SearchResponse } from './search-response';
import { SearchSort } from './search-sort';

/**
 * Handles requests to the `/api/search/` endpoint.
 *
 * Getting results can either be done by individual or filtered by criteria.
 */
class SearchApi {
  public pageSize = 20;

  resource = 'search';

  /**
   * Gets results for selected animal ids.
   * Use case: in catalogue individual page.
   */
  getByIds = async (
    catalogueId: number,
    params: SearchParamsIds, pageParams: Pagination, sortParams: SearchSort, token?: CancelToken,
  ): Promise<SearchResponse> => {
    const {
      animalsIds, db, userType, subIndex, customASBVs,
    } = params;
    const {
      pageNumber, pageSize,
    } = pageParams;
    const {
      sortBy, sortDir,
    } = sortParams;

    const paramObject = createParams({
      ...getFilterParams(db, userType, subIndex, pageNumber, pageSize, sortBy, sortDir),
      [IndividualFilter.AnimalId]: animalsIds,
      ...typeof customASBVs === 'string' ? { customASBVs } : addCustomASBVs(customASBVs), // Accounts for places like pen cards using strings
    });

    paramObject.catalogueId = catalogueId;

    const response = await queryApi.get(
      `/${this.resource}/catalogue`,
      {
        params: paramObject,
        cancelToken: token,
      },
    );

    return plainToClass(SearchResponse, response.data);
  }

  /**
   * Gets the results for a search by criteria.
   */
  getByCriteria = async (filters: FiltersState, db: Database, userType: UserType, subIndex: SubIndex, customASBVs: ASBV[],
    sortBy: string, sortDir: SortDir, token?: CancelToken, pageNumber: number = 1, pageSize: number = this.pageSize): Promise<SearchResponse> => {
    const response = await queryApi.get(
      `/${this.resource}`,
      {
        params: createParams({
          ...getFilterParams(db, userType, subIndex, pageNumber, pageSize, sortBy, sortDir),
          ...getParamsByFilter(filters.basic, basicDefaultState),
          ...getParamsByAsbvFilter(filters.asbv),
          ...getParamsByFilter(filters.pedigree),
          ...getParamsByFilter(filters.progeny),
          ...getParamsByFilter(filters.location, locationDefaultState),
          // Handle breeds separately, Prime SAMM, Dohne and Boer have no values but
          // we need to actually pass a specific breed id.
          [BasicFilter.Breed]: getBreedOrDefault(db, filters.basic),
          ...addCustomASBVs(customASBVs),
        }),
        cancelToken: token,
      },
    );

    return plainToClass(SearchResponse, response.data);
  }

  /**
   * Gets the results for an individual animal search.
   */
  getByIndividual = async (filters: FiltersState, db: Database, userType: UserType, subIndex: SubIndex, customASBVs: ASBV[],
    sortBy: string, sortDir: SortDir, token?: CancelToken, pageNumber?: number, pageSize?: number) => {
    const response = await queryApi.get(
      `/${this.resource}`,
      {
        params: createParams({
          ...getFilterParams(db, userType, subIndex, pageNumber, pageSize, sortBy, sortDir),
          ...getParamsByFilter(filters.individual, individualDefaultState),
          ...addCustomASBVs(customASBVs),
        }),
        cancelToken: token,
      },
    );

    return plainToClass(SearchResponse, response.data);
  }

  /**
   * Gets the search results.
   */
  get = async (params: SearchParams, pageParams: Pagination, sortParams: SearchSort, token?: CancelToken): Promise<SearchResponse> => {
    const {
      category, filters, db, userType, subIndex, customASBVs,
    } = params;
    const {
      pageNumber, pageSize,
    } = pageParams;
    const {
      sortBy, sortDir,
    } = sortParams;

    switch (category) {
      case FilterCategory.Individual:
        return this.getByIndividual(filters, db, userType, subIndex, customASBVs as ASBV[], sortBy, sortDir, token, pageNumber, pageSize);
      case FilterCategory.All:
      default:
        return this.getByCriteria(filters, db, userType, subIndex, customASBVs as ASBV[], sortBy, sortDir, token, pageNumber, pageSize);
    }
  }
}

export default new SearchApi();
